/*
 * TOPランキングセクション（sections/top-ranking.liquid）
 *
 * タブ切替と、2番目以降のタブの遅延読み込みを担当する。
 * 取得先は /collections/<handle>?section_id=top-ranking-panel（Section Rendering API）。
 * DOM 差し替えの手順は global.js の ProductRecommendations を踏襲している。
 *
 * 設計上の約束:
 *  - 差し替えるのは [data-top-ranking-products] の中身だけ。
 *    タブと「もっと見る」には触らないので、切替時にフォーカスを失わない。
 *  - キャッシュは同一ページ滞在中のみ（Map）。在庫・順位が変わるため永続化しない。
 *  - catch の console.error を消さないこと（PR #33 → #35 の事故。CLAUDE.md 参照）。
 */

const TOP_RANKING_SELECTORS = {
  tab: '[data-top-ranking-tab]',
  tablist: '[data-top-ranking-tablist]',
  tabbar: '[data-top-ranking-tabbar]',
  panel: '[data-top-ranking-panel]',
  products: '[data-top-ranking-products]',
  list: '[data-top-ranking-list]',
  scroll: '[data-top-ranking-scroll]',
};

class TopRanking extends HTMLElement {
  constructor() {
    super();

    // 同一ページ滞在中のみのキャッシュ。localStorage 等へ永続化しないこと。
    this.cache = new Map();
    this.controller = null;
    this.requestToken = 0;
    this.resizeObserver = null;
    this.scrollFrame = null;

    this.onTabClick = this.onTabClick.bind(this);
    this.onTabKeydown = this.onTabKeydown.bind(this);
    this.onScrollClick = this.onScrollClick.bind(this);
    this.onTablistScroll = this.onTablistScroll.bind(this);
    this.onBlockSelect = this.onBlockSelect.bind(this);
  }

  connectedCallback() {
    this.tabbar = this.querySelector(TOP_RANKING_SELECTORS.tabbar);
    this.tablist = this.querySelector(TOP_RANKING_SELECTORS.tablist);
    this.tabs = Array.from(this.querySelectorAll(TOP_RANKING_SELECTORS.tab));
    this.panels = Array.from(this.querySelectorAll(TOP_RANKING_SELECTORS.panel));

    if (!this.panels.length) return;

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', this.onTabClick);
      tab.addEventListener('keydown', this.onTabKeydown);
    });

    this.setupScrollButtons();

    document.addEventListener('shopify:block:select', this.onBlockSelect);
  }

  disconnectedCallback() {
    this.abortPending();

    (this.tabs || []).forEach((tab) => {
      tab.removeEventListener('click', this.onTabClick);
      tab.removeEventListener('keydown', this.onTabKeydown);
    });

    (this.scrollButtons || []).forEach((button) => {
      button.removeEventListener('click', this.onScrollClick);
    });

    if (this.tablist) this.tablist.removeEventListener('scroll', this.onTablistScroll);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);

    document.removeEventListener('shopify:block:select', this.onBlockSelect);
  }

  /* ------------------------------------------------------------------
   * タブ切替
   * ------------------------------------------------------------------ */

  onTabClick(event) {
    this.selectTab(event.currentTarget);
  }

  /*
   * 矢印キーはフォーカスの移動のみ（手動アクティベーション）。
   * パネルの中身が非同期取得なので、キーを送るたびに fetch が走るのを避ける。
   * 実際の切替は Enter / Space（button の既定動作で click が発火する）。
   */
  onTabKeydown(event) {
    const currentIndex = this.tabs.indexOf(event.currentTarget);
    if (currentIndex === -1) return;

    let nextIndex = -1;

    switch (event.key) {
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % this.tabs.length;
        break;
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = this.tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();

    const nextTab = this.tabs[nextIndex];
    // roving tabindex: フォーカス可能なタブは常に1つだけ
    event.currentTarget.tabIndex = -1;
    nextTab.tabIndex = 0;
    nextTab.focus();
    this.scrollTabIntoView(nextTab);
  }

  onBlockSelect(event) {
    const blockId = event.detail && event.detail.blockId;
    if (!blockId) return;

    const tab = this.tabs.find((item) => item.dataset.blockId === blockId);
    if (tab) this.selectTab(tab);
  }

  selectTab(tab) {
    const panel = this.panels.find((item) => item.dataset.index === tab.dataset.index);
    if (!panel) return;

    const previous = this.panels.find((item) => !item.hidden);

    // 非表示にするパネルの中にフォーカスが残っているとフォーカスを失うので退避する
    if (previous && previous !== panel && previous.contains(document.activeElement)) {
      tab.focus();
    }

    this.tabs.forEach((item) => {
      const selected = item === tab;
      item.setAttribute('aria-selected', selected ? 'true' : 'false');
      item.tabIndex = selected ? 0 : -1;
      item.classList.toggle('is-active', selected);
    });

    this.panels.forEach((item) => {
      item.hidden = item !== panel;
    });

    this.scrollTabIntoView(tab);
    this.loadPanel(panel);
  }

  /* ------------------------------------------------------------------
   * パネルの遅延読み込み
   * ------------------------------------------------------------------ */

  loadPanel(panel) {
    if (panel.dataset.loaded === 'true') return;

    const container = panel.querySelector(TOP_RANKING_SELECTORS.products);
    const url = panel.dataset.panelUrl;

    if (!container) return;

    // コレクション未設定のタブ。エラー文は出さず「もっと見る」だけ残す。
    if (!url) {
      this.applyMarkup(panel, container, '');
      return;
    }

    const cached = this.cache.get(url);
    if (cached !== undefined) {
      this.applyMarkup(panel, container, cached);
      return;
    }

    // 連打対策: 進行中のリクエストを中断し、さらに識別子で最新レスポンスのみ採用する
    this.abortPending();
    this.controller = new AbortController();
    const token = ++this.requestToken;

    this.showLoading(panel, container);

    fetch(url, { signal: this.controller.signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`TopRanking: ${response.status} ${response.statusText} - ${url}`);
        }
        return response.text();
      })
      .then((text) => {
        if (token !== this.requestToken) return;

        const parsed = new DOMParser().parseFromString(text, 'text/html');
        const list = parsed.querySelector(TOP_RANKING_SELECTORS.list);
        const markup = list && list.children.length ? list.outerHTML : '';

        this.cache.set(url, markup);
        this.applyMarkup(panel, container, markup);
      })
      .catch((error) => {
        // 連打による中断は握り潰す。それ以外は必ずログに残すこと。
        if (error && error.name === 'AbortError') return;

        console.error(error);

        if (token !== this.requestToken) return;

        // loaded を立てないので、タブを開き直せば再試行される
        this.clearContainer(panel, container, 'error');
      });
  }

  applyMarkup(panel, container, markup) {
    container.innerHTML = markup;
    container.removeAttribute('aria-busy');
    panel.dataset.loaded = 'true';
    panel.dataset.state = markup ? 'ready' : 'empty';
  }

  clearContainer(panel, container, state) {
    container.innerHTML = '';
    container.removeAttribute('aria-busy');
    panel.dataset.state = state;
  }

  showLoading(panel, container) {
    container.setAttribute('aria-busy', 'true');
    container.innerHTML = this.skeletonMarkup();
    panel.dataset.state = 'loading';
  }

  skeletonMarkup() {
    const count = Math.max(1, parseInt(this.dataset.skeletonCount, 10) || 5);
    let items = '';

    for (let index = 0; index < count; index++) {
      items +=
        '<li class="top-ranking__item top-ranking__item--skeleton">' +
        '<div class="top-ranking__card">' +
        '<div class="top-ranking__media"></div>' +
        '<div class="top-ranking__info">' +
        '<span class="top-ranking__skeleton-line top-ranking__skeleton-line--short"></span>' +
        '<span class="top-ranking__skeleton-line"></span>' +
        '<span class="top-ranking__skeleton-line top-ranking__skeleton-line--short"></span>' +
        '</div></div></li>';
    }

    return `<ul class="top-ranking__list" aria-hidden="true">${items}</ul>`;
  }

  abortPending() {
    if (this.controller) {
      this.controller.abort();
      this.controller = null;
    }
  }

  /* ------------------------------------------------------------------
   * タブバーの横スクロール
   * ------------------------------------------------------------------ */

  setupScrollButtons() {
    if (!this.tabbar || !this.tablist) return;

    this.scrollButtons = Array.from(this.tabbar.querySelectorAll(TOP_RANKING_SELECTORS.scroll));
    this.scrollButtons.forEach((button) => button.addEventListener('click', this.onScrollClick));

    this.tablist.addEventListener('scroll', this.onTablistScroll);

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.updateScrollButtons());
      this.resizeObserver.observe(this.tablist);
    }

    this.updateScrollButtons();
  }

  onScrollClick(event) {
    const direction = event.currentTarget.dataset.topRankingScroll === 'prev' ? -1 : 1;
    this.tablist.scrollBy({ left: direction * this.tablist.clientWidth * 0.8, behavior: 'smooth' });
  }

  onTablistScroll() {
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    this.scrollFrame = requestAnimationFrame(() => this.updateScrollButtons());
  }

  updateScrollButtons() {
    if (!this.tabbar || !this.tablist || !this.scrollButtons) return;

    const { scrollLeft, clientWidth, scrollWidth } = this.tablist;
    const scrollable = scrollWidth - clientWidth > 1;

    this.tabbar.classList.toggle('top-ranking__tabbar--scrollable', scrollable);

    this.scrollButtons.forEach((button) => {
      if (button.dataset.topRankingScroll === 'prev') {
        button.disabled = !scrollable || scrollLeft <= 1;
      } else {
        button.disabled = !scrollable || scrollLeft + clientWidth >= scrollWidth - 1;
      }
    });
  }

  // タブが見切れている場合だけ横方向に寄せる。
  // scrollIntoView はページ全体を縦スクロールさせることがあるので使わない。
  scrollTabIntoView(tab) {
    if (!this.tablist) return;

    const listRect = this.tablist.getBoundingClientRect();
    const tabRect = tab.getBoundingClientRect();
    const margin = 16;

    if (tabRect.left < listRect.left) {
      this.tablist.scrollBy({ left: tabRect.left - listRect.left - margin, behavior: 'smooth' });
    } else if (tabRect.right > listRect.right) {
      this.tablist.scrollBy({ left: tabRect.right - listRect.right + margin, behavior: 'smooth' });
    }
  }
}

if (!customElements.get('top-ranking')) {
  customElements.define('top-ranking', TopRanking);
}
